import { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  TrashIcon,
  PencilIcon,
  UserGroupIcon,
  KeyIcon,
  ArrowUturnLeftIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchPeople, updatePersonAsync, deletePersonAsync } from '../store/peopleSlice';
import { fetchAssignments, createAssignmentAsync, returnKeyAsync } from '../store/assignmentsSlice';
import { fetchInventory, createInventoryItemAsync } from '../store/keyInventorySlice';
import { Person, KeyAssignment, KeyInventoryItem, KeyType } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import AzureAdUserSearchModal from '../components/AzureAdUserSearchModal';

interface PersonFormData {
  name: string;
  email: string;
  phone: string;
  department: string;
}

interface AssignKeyFormData {
  keyTypeId: string;
  quantity: number;
  notes: string;
}

export default function People() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { people, loading: peopleLoading } = useAppSelector((state) => state.people);
  const { assignments, loading: assignmentsLoading } = useAppSelector((state) => state.assignments);
  const locations = useAppSelector((state) => state.locations.locations);
  const currentLocationId = useAppSelector((state) => state.locations.currentLocationId);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  // Get the current location object
  const currentLocation = useMemo(() => {
    return locations.find(loc => loc.id === currentLocationId);
  }, [locations, currentLocationId]);

  // Determine if user can override location (admin or site-admin)
  const canOverrideLocation = currentUser?.role === 'admin' || currentUser?.role === 'site-admin';

  // Get accessible locations for assignment
  const accessibleLocations = useMemo(() => {
    if (currentUser?.role === 'admin') {
      return locations;
    }
    if (currentUser?.role === 'site-admin' && currentUser.locationAccess) {
      return locations.filter(loc => currentUser.locationAccess.includes(loc.id));
    }
    return currentLocation ? [currentLocation] : [];
  }, [currentUser, locations, currentLocation]);

  const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isAzureAdModalOpen, setIsAzureAdModalOpen] = useState(false);
  const [isNoInventoryWarningOpen, setIsNoInventoryWarningOpen] = useState(false);
  const [pendingAssignmentData, setPendingAssignmentData] = useState<AssignKeyFormData | null>(null);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<KeyAssignment | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [assignmentLocationId, setAssignmentLocationId] = useState<string | null>(null);

  // Search, filter, and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'with-keys' | 'without-keys'>('all');
  const [locationFilter, setLocationFilter] = useState<string[]>([]); // Empty means current location only
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // Initialize location filter to current location when it changes
  useEffect(() => {
    if (currentLocationId && locationFilter.length === 0 && !showAllLocations) {
      setLocationFilter([currentLocationId]);
    }
  }, [currentLocationId]);

  // Update location filter when current location changes (if not showing all)
  useEffect(() => {
    if (!showAllLocations && currentLocationId) {
      setLocationFilter([currentLocationId]);
    }
  }, [currentLocationId, showAllLocations]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Calculate assigned quantities per inventory item
  const assignedByInventoryItem = useMemo(() => {
    const assigned = new Map<string, number>();
    assignments
      .filter(a => a.status === 'active')
      .forEach(a => {
        const current = assigned.get(a.keyInventoryItemId) || 0;
        assigned.set(a.keyInventoryItemId, current + a.quantity);
      });
    return assigned;
  }, [assignments]);

  // Pre-compute assignments by person for O(1) lookup
  const assignmentsByPerson = useMemo(() => {
    const map = new Map<string, KeyAssignment[]>();
    assignments
      .filter(a => a.status === 'active')
      .forEach(a => {
        const existing = map.get(a.personId) || [];
        existing.push(a);
        map.set(a.personId, existing);
      });
    return map;
  }, [assignments]);

  // Pre-compute which locations each person has keys from
  const locationsByPerson = useMemo(() => {
    const map = new Map<string, Set<string>>();
    assignments
      .filter(a => a.status === 'active')
      .forEach(a => {
        const invItem = inventory.find((i: KeyInventoryItem) => i.id === a.keyInventoryItemId);
        if (invItem) {
          const existing = map.get(a.personId) || new Set<string>();
          existing.add(invItem.locationId);
          map.set(a.personId, existing);
        }
      });
    return map;
  }, [assignments, inventory]);

  const personForm = useForm<PersonFormData>();
  const assignForm = useForm<AssignKeyFormData>();

  useEffect(() => {
    dispatch(fetchPeople());
    dispatch(fetchAssignments());
    dispatch(fetchInventory());
  }, [dispatch]);

  const handleOpenPersonModal = (person: Person) => {
    setEditingPerson(person);
    personForm.setValue('name', person.name);
    personForm.setValue('email', person.email || '');
    personForm.setValue('phone', person.phone || '');
    personForm.setValue('department', person.department || '');
    setIsPersonModalOpen(true);
  };

  const handleClosePersonModal = () => {
    setIsPersonModalOpen(false);
    setEditingPerson(null);
    personForm.reset();
  };

  const handleOpenAssignModal = (person: Person) => {
    setSelectedPerson(person);
    setAssignmentLocationId(currentLocationId);
    assignForm.reset({ quantity: 1 });
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedPerson(null);
    setAssignmentLocationId(null);
    assignForm.reset();
  };

  const handleOpenReturnModal = (assignment: KeyAssignment) => {
    setSelectedAssignment(assignment);
    setReturnNotes('');
    setIsReturnModalOpen(true);
  };

  const handleCloseReturnModal = () => {
    setIsReturnModalOpen(false);
    setSelectedAssignment(null);
    setReturnNotes('');
  };

  const onSubmitPerson = async (data: PersonFormData) => {
    if (!editingPerson) return;
    
    setIsSaving(true);
    const now = new Date().toISOString();

    try {
      const updatedPerson: Person = {
        ...editingPerson,
        ...data,
        updatedAt: now,
      };
      await dispatch(updatePersonAsync(updatedPerson)).unwrap();
      handleClosePersonModal();
    } catch (error) {
      console.error('Failed to save person:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmitAssignment = async (data: AssignKeyFormData) => {
    const targetLocationId = assignmentLocationId || currentLocationId;
    if (!selectedPerson || !currentUser || !targetLocationId) return;

    // Find existing inventory item for this key type and target location
    const inventoryItem = inventory.find(
      (item: KeyInventoryItem) => item.keyTypeId === data.keyTypeId && item.locationId === targetLocationId
    );

    // Calculate available inventory
    let availableQuantity = 0;
    if (inventoryItem) {
      const assigned = assignedByInventoryItem.get(inventoryItem.id) || 0;
      availableQuantity = inventoryItem.quantity - assigned;
    }

    // If not enough inventory, show warning
    if (availableQuantity < data.quantity) {
      setPendingAssignmentData(data);
      setIsNoInventoryWarningOpen(true);
      return;
    }

    // Proceed with assignment
    await performAssignment(data, false);
  };

  const performAssignment = async (data: AssignKeyFormData, _allowNegativeInventory: boolean) => {
    const targetLocationId = assignmentLocationId || currentLocationId;
    if (!selectedPerson || !currentUser || !targetLocationId) return;

    setIsSaving(true);

    try {
      const timestamp = new Date().toISOString();
      
      // Find existing inventory item for this key type and target location
      let inventoryItem = inventory.find(
        (item: KeyInventoryItem) => item.keyTypeId === data.keyTypeId && item.locationId === targetLocationId
      );
      
      // If no inventory item exists, create one with 0 quantity
      // The assignment will make the available count negative
      if (!inventoryItem) {
        const newInventoryItem: KeyInventoryItem = {
          id: `inv-${Date.now()}`,
          keyTypeId: data.keyTypeId,
          locationId: targetLocationId,
          quantity: 0,
          lastUpdated: timestamp,
          updatedBy: currentUser.id,
        };
        inventoryItem = await dispatch(createInventoryItemAsync(newInventoryItem)).unwrap();
      }
      
      const newAssignment: KeyAssignment = {
        id: `assignment-${Date.now()}`,
        keyInventoryItemId: inventoryItem.id,
        personId: selectedPerson.id,
        quantity: data.quantity,
        assignedAt: timestamp,
        assignedBy: currentUser.name,
        notes: data.notes,
        status: 'active',
      };
      await dispatch(createAssignmentAsync(newAssignment)).unwrap();
      handleCloseAssignModal();
      setIsNoInventoryWarningOpen(false);
      setPendingAssignmentData(null);
    } catch (error) {
      console.error('Failed to assign key:', error);
      alert('Failed to assign key. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmNoInventoryAssignment = async () => {
    if (pendingAssignmentData) {
      await performAssignment(pendingAssignmentData, true);
    }
  };

  const handleCancelNoInventoryWarning = () => {
    setIsNoInventoryWarningOpen(false);
    setPendingAssignmentData(null);
  };

  const handleReturnKey = async () => {
    if (!selectedAssignment || !currentUser) return;

    setIsSaving(true);

    try {
      await dispatch(returnKeyAsync({
        id: selectedAssignment.id,
        returnedBy: currentUser.name,
        notes: returnNotes,
      })).unwrap();
      handleCloseReturnModal();
    } catch (error) {
      console.error('Failed to return key:', error);
      alert('Failed to return key. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePerson = async (person: Person) => {
    const activeAssignments = assignments.filter(
      (a) => a.personId === person.id && a.status === 'active'
    );

    if (activeAssignments.length > 0) {
      alert(`Cannot delete ${person.name}. They have ${activeAssignments.length} active key assignment(s). Please return all keys first.`);
      return;
    }

    if (confirm(`Are you sure you want to remove ${person.name}?`)) {
      try {
        await dispatch(deletePersonAsync(person.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete person:', error);
        alert('Failed to delete. Please try again.');
      }
    }
  };

  const getPersonAssignments = useCallback((personId: string) => {
    return assignmentsByPerson.get(personId) || [];
  }, [assignmentsByPerson]);

  const getInventoryItem = (itemId: string): KeyInventoryItem | undefined => {
    return inventory.find((i: KeyInventoryItem) => i.id === itemId);
  };

  const getKeyType = (keyTypeId: string): KeyType | undefined => {
    return keyTypes.find((kt: KeyType) => kt.id === keyTypeId);
  };

  const getLocationForInventoryItem = (inventoryItemId: string) => {
    const invItem = inventory.find((i: KeyInventoryItem) => i.id === inventoryItemId);
    if (!invItem) return null;
    return locations.find(loc => loc.id === invItem.locationId);
  };

  const loading = peopleLoading || assignmentsLoading;
  const activePeople = useMemo(() => people.filter((p) => p.status === 'active'), [people]);

  // Filter and search people
  const filteredPeople = useMemo(() => {
    let result = activePeople;

    // Apply location filter (show people with keys from selected locations, or people without keys)
    if (locationFilter.length > 0) {
      result = result.filter(p => {
        const personLocations = locationsByPerson.get(p.id);
        // Include people with no keys (they can be assigned keys from current location)
        if (!personLocations || personLocations.size === 0) return true;
        // Include people who have keys from any of the filtered locations
        return locationFilter.some(locId => personLocations.has(locId));
      });
    }

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        (p.email && p.email.toLowerCase().includes(searchLower)) ||
        (p.department && p.department.toLowerCase().includes(searchLower))
      );
    }

    // Apply key filter
    if (filterMode === 'with-keys') {
      result = result.filter(p => (assignmentsByPerson.get(p.id)?.length || 0) > 0);
    } else if (filterMode === 'without-keys') {
      result = result.filter(p => (assignmentsByPerson.get(p.id)?.length || 0) === 0);
    }

    // Sort by name for consistent ordering
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [activePeople, debouncedSearch, filterMode, assignmentsByPerson, locationFilter, locationsByPerson]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPeople.length / ITEMS_PER_PAGE);
  const paginatedPeople = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPeople.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPeople, currentPage]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterMode, locationFilter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">People</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Manage people and their key assignments ({activePeople.length} total)
          </p>
        </div>
        <Button
          onClick={() => setIsAzureAdModalOpen(true)}
          disabled={loading}
        >
          <CloudArrowDownIcon className="w-5 h-5 mr-2" />
          Add from Azure AD
        </Button>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Search by name, email, or department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-secondary-400" />
          <div className="flex rounded-lg border border-secondary-300 dark:border-secondary-600 overflow-hidden">
            <button
              onClick={() => setFilterMode('all')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                filterMode === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterMode('with-keys')}
              className={`px-3 py-2 text-sm font-medium border-l border-secondary-300 dark:border-secondary-600 transition-colors ${
                filterMode === 'with-keys'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              With Keys
            </button>
            <button
              onClick={() => setFilterMode('without-keys')}
              className={`px-3 py-2 text-sm font-medium border-l border-secondary-300 dark:border-secondary-600 transition-colors ${
                filterMode === 'without-keys'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              Without Keys
            </button>
          </div>
        </div>
      </div>

      {/* Location Filter - for admin/site-admin */}
      {canOverrideLocation && accessibleLocations.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <MapPinIcon className="w-5 h-5 text-secondary-400" />
          <span className="text-sm text-secondary-600 dark:text-secondary-400 mr-1">Location:</span>
          {accessibleLocations.map((loc) => {
            const locColor = loc.color || '#6B7280';
            const isSelected = locationFilter.includes(loc.id);
            return (
              <button
                key={loc.id}
                onClick={() => {
                  if (isSelected && locationFilter.length === 1) {
                    // Can't deselect the last one, but can show all
                    setShowAllLocations(true);
                    setLocationFilter(accessibleLocations.map(l => l.id));
                  } else if (isSelected) {
                    setShowAllLocations(false);
                    setLocationFilter(locationFilter.filter(id => id !== loc.id));
                  } else {
                    setShowAllLocations(false);
                    setLocationFilter([...locationFilter, loc.id]);
                  }
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full border-2 transition-colors ${
                  isSelected
                    ? 'text-secondary-900 dark:text-secondary-100'
                    : 'border-secondary-200 dark:border-secondary-600 text-secondary-500 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                }`}
                style={isSelected ? {
                  borderColor: locColor,
                  backgroundColor: `${locColor}15`
                } : undefined}
              >
                <span 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: locColor }}
                />
                {loc.name}
              </button>
            );
          })}
          {locationFilter.length < accessibleLocations.length && (
            <button
              onClick={() => {
                setShowAllLocations(true);
                setLocationFilter(accessibleLocations.map(l => l.id));
                setCurrentPage(1);
              }}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline ml-2"
            >
              Show all
            </button>
          )}
        </div>
      )}

      {/* Current location indicator for non-admin users */}
      {!canOverrideLocation && currentLocation && (
        <div className="flex items-center gap-2">
          <MapPinIcon className="w-5 h-5 text-secondary-400" />
          <span className="text-sm text-secondary-600 dark:text-secondary-400">Showing people with keys from:</span>
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full border-2"
            style={{
              borderColor: currentLocation.color || '#6B7280',
              backgroundColor: `${currentLocation.color || '#6B7280'}15`
            }}
          >
            <span 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: currentLocation.color || '#6B7280' }}
            />
            {currentLocation.name}
          </span>
        </div>
      )}

      {/* Results Count */}
      {(debouncedSearch || filterMode !== 'all' || (canOverrideLocation && locationFilter.length !== accessibleLocations.length)) && (
        <p className="text-sm text-secondary-600 dark:text-secondary-400">
          Showing {filteredPeople.length} of {activePeople.length} people
          {debouncedSearch && ` matching "${debouncedSearch}"`}
        </p>
      )}

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
        </div>
      )}

      {/* People List - Compact */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 divide-y divide-secondary-200 dark:divide-secondary-700">
        {paginatedPeople.map((person) => {
          const personAssignments = getPersonAssignments(person.id);

          return (
            <div key={person.id} className="flex items-center px-4 py-2 hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
              {/* Photo/Icon */}
              {person.photoUrl ? (
                <img
                  src={person.photoUrl}
                  alt={person.name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <UserGroupIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
              )}

              {/* Name & Email */}
              <div className="ml-3 min-w-0 w-48 sm:w-56 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                    {person.name}
                  </span>
                  {person.department && (
                    <span className="hidden sm:inline-block px-1.5 py-0.5 text-xs bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400 rounded">
                      {person.department}
                    </span>
                  )}
                </div>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                  {person.email}
                </p>
              </div>

              {/* Assigned Keys */}
              <div className="hidden md:flex flex-1 items-center gap-1.5 mx-4 overflow-x-auto">
                {personAssignments.length > 0 ? (
                  personAssignments.map((assignment) => {
                    const invItem = getInventoryItem(assignment.keyInventoryItemId);
                    const keyType = invItem ? getKeyType(invItem.keyTypeId) : null;
                    const location = getLocationForInventoryItem(assignment.keyInventoryItemId);
                    const locationColor = location?.color || '#6B7280';
                    return keyType ? (
                      <button
                        key={assignment.id}
                        onClick={() => navigate(`/inventory/key-type/${keyType.id}`)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap border hover:opacity-80 transition-opacity"
                        style={{ 
                          backgroundColor: `${locationColor}15`,
                          borderColor: `${locationColor}40`,
                          color: locationColor
                        }}
                        title={`${keyType.name}${assignment.quantity > 1 ? ` (x${assignment.quantity})` : ''} - ${location?.name || 'Unknown location'} - Click to view details`}
                      >
                        <KeyIcon className="w-3 h-3" style={{ color: locationColor }} />
                        {keyType.name}
                        {assignment.quantity > 1 && <span style={{ opacity: 0.7 }}>x{assignment.quantity}</span>}
                      </button>
                    ) : null;
                  })
                ) : (
                  <span className="text-xs text-secondary-400 dark:text-secondary-500 italic">No keys assigned</span>
                )}
              </div>

              {/* Keys Badge */}
              <div className="flex items-center gap-1 mx-2 flex-shrink-0">
                <button
                  onClick={() => handleOpenAssignModal(person)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                    personAssignments.length > 0
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400'
                  } hover:opacity-80`}
                  title={personAssignments.length > 0 
                    ? `${personAssignments.length} key(s) assigned - Click to assign more`
                    : 'Click to assign a key'
                  }
                >
                  <KeyIcon className="w-3 h-3" />
                  <span>{personAssignments.length}</span>
                </button>
                {personAssignments.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedPerson(person);
                      if (personAssignments.length === 1) {
                        handleOpenReturnModal(personAssignments[0]);
                      } else {
                        // For multiple keys, open assign modal which shows current keys
                        handleOpenAssignModal(person);
                      }
                    }}
                    className="p-1 text-xs text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                    title="Return key"
                  >
                    <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  onClick={() => handleOpenPersonModal(person)}
                  className="p-1.5 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                  title="Edit"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeletePerson(person)}
                  className="p-1.5 text-secondary-400 hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-secondary-200 dark:border-secondary-700 pt-4">
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredPeople.length)} of {filteredPeople.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1">
              {/* Show page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {filteredPeople.length === 0 && !loading && (
        <Card>
          <div className="py-12 text-center">
            <UserGroupIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">
              {debouncedSearch || filterMode !== 'all' ? 'No matching people' : 'No people'}
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {debouncedSearch || filterMode !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Add people from Azure AD to assign keys.'}
            </p>
            {(debouncedSearch || filterMode !== 'all') && (
              <button
                onClick={() => { setSearchQuery(''); setFilterMode('all'); }}
                className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Clear filters
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Edit Person Modal */}
      <Modal
        isOpen={isPersonModalOpen}
        onClose={handleClosePersonModal}
        title="Edit Person"
        footer={
          <>
            <Button variant="secondary" onClick={handleClosePersonModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={personForm.handleSubmit(onSubmitPerson)} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Update'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              {...personForm.register('name', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="John Doe"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Email
            </label>
            <input
              type="email"
              {...personForm.register('email')}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="john@example.com"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Phone
            </label>
            <input
              type="tel"
              {...personForm.register('phone')}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="(555) 123-4567"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Department
            </label>
            <input
              type="text"
              {...personForm.register('department')}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Engineering"
              disabled={isSaving}
            />
          </div>
        </form>
      </Modal>

      {/* Assign Key Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        title={`Assign Key to ${selectedPerson?.name || ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseAssignModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={assignForm.handleSubmit(onSubmitAssignment)} disabled={isSaving || !assignmentLocationId}>
              {isSaving ? 'Assigning...' : 'Assign Key'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* Location selector - editable for admin/site-admin, read-only for others */}
          {canOverrideLocation && accessibleLocations.length > 1 ? (
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Assign from Location *
              </label>
              <div className="space-y-2">
                {accessibleLocations.map((loc) => {
                  const locColor = loc.color || '#6B7280';
                  const isSelected = assignmentLocationId === loc.id;
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => setAssignmentLocationId(loc.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors text-left ${
                        isSelected
                          ? 'text-secondary-900 dark:text-secondary-100'
                          : 'border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                      }`}
                      style={isSelected ? {
                        borderColor: locColor,
                        backgroundColor: `${locColor}15`
                      } : undefined}
                      disabled={isSaving}
                    >
                      <span 
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: locColor }}
                      />
                      <span className="font-medium">{loc.name}</span>
                      {isSelected && (
                        <span 
                          className="ml-auto text-xs font-medium px-2 py-0.5 rounded"
                          style={{ color: locColor, backgroundColor: `${locColor}20` }}
                        >
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div 
              className="p-3 rounded-lg border-l-4"
              style={{ 
                backgroundColor: currentLocation?.color ? `${currentLocation.color}10` : undefined,
                borderLeftColor: currentLocation?.color || '#6B7280'
              }}
            >
              <div className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">
                Assigning from Location
              </div>
              <div className="flex items-center gap-2">
                <span 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currentLocation?.color || '#6B7280' }}
                />
                <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                  {currentLocation?.name || 'No location selected'}
                </span>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Key Type *
            </label>
            <select
              {...assignForm.register('keyTypeId', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            >
              <option value="">Select a key type</option>
              {keyTypes.map((keyType: KeyType) => (
                <option key={keyType.id} value={keyType.id}>
                  {keyType.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              {...assignForm.register('quantity', { required: true, min: 1 })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Notes
            </label>
            <textarea
              {...assignForm.register('notes')}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional notes about this assignment"
              rows={2}
              disabled={isSaving}
            />
          </div>
        </form>
      </Modal>

      {/* Return Key Modal */}
      <Modal
        isOpen={isReturnModalOpen}
        onClose={handleCloseReturnModal}
        title="Return Key"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseReturnModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleReturnKey} disabled={isSaving}>
              {isSaving ? 'Returning...' : 'Confirm Return'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Are you sure you want to mark this key as returned?
          </p>
          {selectedAssignment && (
            <div className="p-3 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
              <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                {(() => {
                  const item = getInventoryItem(selectedAssignment.keyInventoryItemId);
                  const keyType = item ? getKeyType(item.keyTypeId) : null;
                  return `${keyType?.name || 'Unknown'}`;
                })()}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                Quantity: {selectedAssignment.quantity}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Return Notes (optional)
            </label>
            <textarea
              value={returnNotes}
              onChange={(e) => setReturnNotes(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any notes about the return"
              rows={2}
              disabled={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* No Inventory Warning Modal */}
      <Modal
        isOpen={isNoInventoryWarningOpen}
        onClose={handleCancelNoInventoryWarning}
        title="Insufficient Inventory Warning"
        footer={
          <>
            <Button variant="secondary" onClick={handleCancelNoInventoryWarning} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmNoInventoryAssignment} disabled={isSaving}>
              {isSaving ? 'Assigning...' : 'Assign Anyway'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-secondary-700 dark:text-secondary-300">
                There is <strong>insufficient inventory</strong> for this key assignment. Proceeding will create a negative inventory balance.
              </p>
            </div>
          </div>
          {pendingAssignmentData && (() => {
            const targetLocationId = assignmentLocationId || currentLocationId;
            const targetLocation = locations.find(loc => loc.id === targetLocationId);
            const locColor = targetLocation?.color || '#6B7280';
            return (
            <div 
              className="p-3 rounded-lg border-l-4"
              style={{ 
                backgroundColor: `${locColor}10`,
                borderLeftColor: locColor
              }}
            >
              <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                {keyTypes.find(kt => kt.id === pendingAssignmentData.keyTypeId)?.name || 'Unknown Key'}
              </p>
              <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                <span 
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: locColor }}
                />
                Location: {targetLocation?.name || 'Unknown'}
              </p>
              <p className="text-xs text-secondary-600 dark:text-secondary-400">
                Quantity to assign: {pendingAssignmentData.quantity}
              </p>
              {(() => {
                const invItem = inventory.find(
                  (item: KeyInventoryItem) => item.keyTypeId === pendingAssignmentData.keyTypeId && item.locationId === targetLocationId
                );
                const assigned = invItem ? (assignedByInventoryItem.get(invItem.id) || 0) : 0;
                const available = invItem ? invItem.quantity - assigned : 0;
                return (
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                    Currently available: {available}
                  </p>
                );
              })()}
            </div>
            );
          })()}
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            This may indicate that physical keys need to be ordered or transferred from another location.
          </p>
        </div>
      </Modal>

      {/* Azure AD User Search Modal */}
      <AzureAdUserSearchModal
        isOpen={isAzureAdModalOpen}
        onClose={() => setIsAzureAdModalOpen(false)}
        onPersonAdded={() => {
          // Refresh the people list after adding
          dispatch(fetchPeople());
        }}
      />
    </div>
  );
}
